// src/PDF/ReceiptPDF.jsx
import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

const money = (n) => {
  const v = Number(n || 0);
  return `₦${v.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const safe = (x) => (x ? String(x) : "—");

const styles = StyleSheet.create({
  page: {
    padding: 32,
    fontSize: 10.5,
    color: "#111827",
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
    position: "relative",
  },

  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 14,
    marginBottom: 16,
  },
  brandLeft: { flex: 1 },
  titleRight: { width: 220, alignItems: "flex-end" },

  logo: { width: 84, height: 36, objectFit: "contain", marginBottom: 8 },

  h1: { fontSize: 18, fontWeight: 700, marginBottom: 4 },
  companyName: { fontSize: 12.5, fontWeight: 700, marginBottom: 4 },

  smallMuted: { color: "#6B7280" },
  strong: { fontWeight: 700 },

  block: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
    marginBottom: 12,
  },
  blockTitle: { fontSize: 11, fontWeight: 700, marginBottom: 8 },

  twoCol: { flexDirection: "row", gap: 12 },
  col: { flex: 1 },

  kvRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    gap: 10,
  },
  kvKey: { color: "#374151" },
  kvVal: { fontWeight: 700, flexShrink: 1, textAlign: "right" },

  table: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    overflow: "hidden",
    marginBottom: 12,
  },
  thead: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  th: { fontWeight: 700, color: "#111827" },
  tbodyRow: {
    flexDirection: "row",
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },

  cDesc: { flex: 1.9, paddingRight: 8 },
  cQty: { width: 52, textAlign: "right" },
  cRate: { width: 86, textAlign: "right" },
  cAmt: { width: 92, textAlign: "right" },

  totalsWrap: { flexDirection: "row", justifyContent: "flex-end" },
  totalsBox: {
    width: 260,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    padding: 12,
  },
  totalLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },
  grandLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    paddingTop: 8,
    marginTop: 6,
  },
  grandText: { fontSize: 12, fontWeight: 800 },

  statusPill: (accent) => ({
    alignSelf: "flex-start",
    borderWidth: 1,
    borderColor: accent,
    backgroundColor: "#ffffff",
    color: accent,
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 999,
    fontSize: 9.5,
    fontWeight: 700,
    marginTop: 6,
    textTransform: "uppercase",
  }),

  // ✅ PAID stamp (drawn with text so no image dependency)
  paidStamp: (accent) => ({
    position: "absolute",
    top: 86,
    right: 40,
    transform: "rotate(-18deg)",
    borderWidth: 2,
    borderColor: accent,
    borderRadius: 8,
    paddingVertical: 6,
    paddingHorizontal: 12,
    opacity: 0.18,
  }),
  paidStampText: (accent) => ({
    color: accent,
    fontSize: 32,
    fontWeight: 800,
    letterSpacing: 2,
  }),

  footer: {
    marginTop: 14,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
    textAlign: "center",
    color: "#6B7280",
  },
});

export default function ReceiptPDF({
  companyInfo,
  receiptData,
  accent = "#008080",
  paymentInstructions,
}) {
  const total = Number(receiptData?.total || 0);

  // ✅ RECEIPTS ARE ALWAYS PAID
  const isPaid = true;
  const statusLabel = "PAID";

  const items = Array.isArray(receiptData?.items) ? receiptData.items : [];

  const defaultPayment = `Payment received successfully. Receipt #: ${safe(
    receiptData?.receiptNumber
  )}.`;

  const paymentText =
    paymentInstructions && String(paymentInstructions).trim()
      ? String(paymentInstructions)
      : defaultPayment;

  return (
    <Document title={safe(receiptData?.receiptNumber)}>
      <Page size="A4" style={styles.page}>
        {/* ✅ Stamp */}
        {isPaid ? (
          <View style={styles.paidStamp(accent)}>
            <Text style={styles.paidStampText(accent)}>PAID</Text>
          </View>
        ) : null}

        {/* Header */}
        <View style={styles.headerRow}>
          <View style={styles.brandLeft}>
            {companyInfo?.logo_url ? (
              <Image style={styles.logo} src={companyInfo.logo_url} />
            ) : null}

            <Text style={styles.companyName}>
              {safe(companyInfo?.company_name)}
            </Text>
            <Text style={styles.smallMuted}>{safe(companyInfo?.address)}</Text>

            {!!companyInfo?.phone_number && (
              <Text style={styles.smallMuted}>
                Phone: {safe(companyInfo.phone_number)}
              </Text>
            )}
            {!!companyInfo?.email && (
              <Text style={styles.smallMuted}>{safe(companyInfo.email)}</Text>
            )}
            {!!companyInfo?.TIN && (
              <Text style={styles.smallMuted}>
                TIN: {safe(companyInfo.TIN)}
              </Text>
            )}
          </View>

          <View style={styles.titleRight}>
            <Text style={styles.h1}>RECEIPT</Text>

            <Text style={{ fontSize: 11 }}>
              <Text style={styles.smallMuted}>Receipt #</Text>{" "}
              <Text style={styles.strong}>
                {safe(receiptData?.receiptNumber)}
              </Text>
            </Text>

            <Text style={{ fontSize: 10.5, marginTop: 4 }}>
              <Text style={styles.smallMuted}>Payment Date:</Text>{" "}
              <Text style={styles.strong}>{safe(receiptData?.date)}</Text>
            </Text>

            <Text style={styles.statusPill(accent)}>{statusLabel}</Text>
          </View>
        </View>

        {/* Recipient */}
        <View style={styles.twoCol}>
          <View style={[styles.block, styles.col]}>
            <Text style={styles.blockTitle}>Recipient Information</Text>
            <View style={styles.kvRow}>
              <Text style={styles.kvKey}>Customer</Text>
              <Text style={styles.kvVal}>
                {safe(receiptData?.customerName)}
              </Text>
            </View>
            <View style={styles.kvRow}>
              <Text style={styles.kvKey}>Address</Text>
              <Text style={styles.kvVal}>
                {safe(receiptData?.customerAddress)}
              </Text>
            </View>
          </View>
        </View>

        {/* Items */}
        <Text style={{ fontSize: 11, fontWeight: 700, marginBottom: 8 }}>
          Items / Services Paid For
        </Text>

        <View style={styles.table}>
          <View style={styles.thead}>
            <Text style={[styles.th, styles.cDesc]}>Description</Text>
            <Text style={[styles.th, styles.cQty]}>Qty</Text>
            <Text style={[styles.th, styles.cRate]}>Rate</Text>
            <Text style={[styles.th, styles.cAmt]}>Amount</Text>
          </View>

          {items.length ? (
            items.map((it, idx) => {
              const qty = Number(it.quantity || 0);
              const rate = Number(it.rate || 0);
              const amt =
                Number(it.amount || 0) || Number(qty) * Number(rate) || 0;

              return (
                <View key={idx} style={styles.tbodyRow} wrap={false}>
                  <Text style={styles.cDesc}>{safe(it.description)}</Text>
                  <Text style={styles.cQty}>{qty || 0}</Text>
                  <Text style={styles.cRate}>{money(rate)}</Text>
                  <Text style={styles.cAmt}>{money(amt)}</Text>
                </View>
              );
            })
          ) : (
            <View style={styles.tbodyRow}>
              <Text style={{ color: "#6B7280" }}>No items.</Text>
            </View>
          )}
        </View>

        {/* Notes + Total */}
        <View style={styles.twoCol}>
          <View style={[styles.block, styles.col]}>
            <Text style={styles.blockTitle}>Notes</Text>
            <Text style={{ color: "#374151", lineHeight: 1.4 }}>
              {receiptData?.notes ? String(receiptData.notes) : "—"}
            </Text>
          </View>

          <View style={styles.totalsWrap}>
            <View style={styles.totalsBox}>
              <View style={styles.grandLine}>
                <Text style={styles.grandText}>Total Paid</Text>
                <Text style={[styles.grandText, { color: accent }]}>
                  {money(total)}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* ✅ Payment instructions block */}
        <View style={styles.block}>
          <Text style={styles.blockTitle}>Payment Instructions</Text>
          <Text style={{ color: "#374151", lineHeight: 1.4 }}>
            {paymentText}
          </Text>
        </View>

        <Text style={styles.footer}>Thank you for your business.</Text>
      </Page>
    </Document>
  );
}
